import { jsx, jsxs } from "react/jsx-runtime";
import { findDOMNode } from "react-dom";
import { createRoot } from "react-dom/client";
import { getRangeLength } from "@teselagen/range-utils";
import Popper from "popper.js";
import {
  getInsertBetweenVals,
  convertDnaCaretPositionOrRangeToAA,
  filterSequenceString
} from "@teselagen/sequence-utils";
import React from "react";
import { divideBy3 } from "../utils/proteinUtils";
import "./createSequenceInputPopupStyle.css";
import { Classes } from "@blueprintjs/core";
import { getNodeToRefocus } from "../utils/editorUtils";
import { noop } from "lodash-es";
let div;
class SequenceInputNoHotkeys extends React.Component {
  state = {
    charsToInsert: "",
    hasTempError: false
  };
  componentDidMount() {
    document.addEventListener(
      "mousedown",
      this.handleUnmountIfClickOustidePopup
    );
  }
  componentWillUnmount() {
    document.removeEventListener(
      "mousedown",
      this.handleUnmountIfClickOustidePopup
    );
  }
  handleUnmountIfClickOustidePopup = (e) => {
    const n = findDOMNode(this);
    if (!n) return;
    const node = n.parentNode;
    if (!node) return;
    if (node.contains(e.target)) {
      return;
    }
    this.handleUnmount();
  };
  handleUnmount = () => {
    setTimeout(() => {
      const n = findDOMNode(this);
      if (!n) return;
      const node = n.parentNode;
      if (!node) return;
      if (node._root) {
        node._root.unmount();
        delete node._root;
      }
      this.props.nodeToReFocus && this.props.nodeToReFocus.focus();
      document.getElementById("sequenceInputBubble").outerHTML = "";
    });
  };
  handleInsert() {
    const { handleInsert = noop, isProtein } = this.props;
    const { charsToInsert } = this.state;
    if (!charsToInsert.length) {
      return;
    }
    const seqToInsert = isProtein ? {
      proteinSequence: charsToInsert,
      isProtein: true
    } : {
      sequence: charsToInsert
    };
    handleInsert(seqToInsert);
  }
  render() {
    const {
      isReplace,
      selectionLayer,
      sequenceLength,
      isProtein,
      caretPosition,
      sequenceData,
      maxInsertSize,
      getAcceptedInsertChars,
      showAminoAcidUnitAsCodon
    } = this.props;
    const { charsToInsert, hasTempError } = this.state;
    let message;
    if (isReplace) {
      const betweenVals = getInsertBetweenVals(
        -1,
        selectionLayer,
        sequenceLength
      );
      message = /* @__PURE__ */ jsxs("span", { children: [
        "Press ",
        /* @__PURE__ */ jsx("span", { style: { fontWeight: "bolder" }, children: "ENTER" }),
        " to replace",
        " ",
        divideBy3(getRangeLength(selectionLayer, sequenceLength), isProtein),
        " ",
        isProtein ? showAminoAcidUnitAsCodon ? "codons" : "AAs" : "base pairs",
        " ",
        "between",
        " ",
        isProtein ? convertDnaCaretPositionOrRangeToAA(betweenVals[0]) : betweenVals[0],
        " ",
        "and",
        " ",
        isProtein ? convertDnaCaretPositionOrRangeToAA(betweenVals[1] + 2) : betweenVals[1]
      ] });
    } else {
      message = /* @__PURE__ */ jsxs("span", { children: [
        "Press ",
        /* @__PURE__ */ jsx("span", { style: { fontWeight: "bolder" }, children: "ENTER" }),
        " to insert",
        " ",
        charsToInsert.length,
        " ",
        isProtein ? `${showAminoAcidUnitAsCodon ? "codons" : "AAs"}` : "base pairs",
        " ",
        "after",
        " ",
        isProtein ? `${showAminoAcidUnitAsCodon ? "codon" : "AA"}` : "base",
        " ",
        isProtein ? convertDnaCaretPositionOrRangeToAA(caretPosition) : caretPosition
      ] });
    }
    return /* @__PURE__ */ jsxs("div", { className: "sequenceInputBubble", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          autoCorrect: "off",
          onKeyDown: (e) => {
            if (e.keyCode === 27) {
              this.handleUnmount();
            }
            if (e.keyCode === 13) {
              this.handleInsert();
              this.handleUnmount();
            }
          },
          className: Classes.INPUT,
          value: charsToInsert,
          autoFocus: true,
          style: hasTempError ? { borderColor: "red" } : {},
          onChange: (e) => {
            const [sanitizedVal, warnings] = filterSequenceString(
              e.target.value,
              {
                ...sequenceData,
                name: void 0,
                getAcceptedInsertChars
              }
            );
            if (warnings.length) {
              this.setState({
                hasTempError: true
              });
              setTimeout(() => {
                this.setState({
                  hasTempError: false
                });
              }, 200);
            }
            if (maxInsertSize && sanitizedVal.length > maxInsertSize) {
              return window.toastr.error(
                `Sorry, your insert is greater than ${maxInsertSize}`
              );
            }
            e.target.value = sanitizedVal;
            this.setState({ charsToInsert: sanitizedVal });
          }
        }
      ),
      /* @__PURE__ */ jsx("div", { style: { marginTop: 10 }, children: message }),
      /* @__PURE__ */ jsxs("div", { style: { marginTop: 10 }, children: [
        "Press ",
        /* @__PURE__ */ jsx("span", { style: { fontWeight: "bolder" }, children: "ESC" }),
        " to",
        " ",
        /* @__PURE__ */ jsx("button", { className: "link-button", onClick: this.handleUnmount, children: "cancel" })
      ] })
    ] });
  }
}
export default function createSequenceInputPopup(props) {
  const { useEventPositioning } = props;
  let caretEl;
  if (useEventPositioning) {
    const { e, nodeToReFocus } = useEventPositioning;
    const top = e.clientY;
    const right = e.clientX;
    const bottom = e.clientY;
    const left = e.clientX;
    caretEl = {
      nodeToRefocus: nodeToReFocus,
      getBoundingClientRect: () => ({
        top,
        right,
        bottom,
        left
      }),
      clientWidth: 0,
      clientHeight: 0
    };
  }
  if (!caretEl || !caretEl === 0 || !isElementInViewport(caretEl)) {
    const activeEl = getActiveElement();
    if (activeEl) {
      caretEl = activeEl.querySelector(".veCaret");
    }
  }
  if (!caretEl || !caretEl === 0 || !isElementInViewport(caretEl)) {
    caretEl = getActiveElement();
  }
  if (!caretEl || !caretEl === 0 || !isElementInViewport(caretEl)) {
    caretEl = document.querySelector(".veCaret");
  }
  if (document.body.classList.contains("sequenceDragging")) {
    window.toastr.warning("Can't insert new sequence while dragging");
    return;
  }
  if (document.getElementById("sequenceInputBubble")) {
    document.getElementById("sequenceInputBubble").outerHTML = "";
  }
  div = document.createElement("div");
  div.style.zIndex = "400000";
  div.id = "sequenceInputBubble";
  document.body.appendChild(div);
  const innerEl = /* @__PURE__ */ jsx(
    SequenceInputNoHotkeys,
    {
      nodeToReFocus: caretEl.nodeToRefocus || getNodeToRefocus(caretEl),
      ...props
    }
  );
  if (!div._root) {
    div._root = createRoot(div);
  }
  div._root.render(innerEl);
  if (!caretEl) {
    return console.error(
      "there must be a caret element present in order to display the insertSequence popup"
    );
  }
  new Popper(caretEl, div, {
    placement: "bottom",
    modifiers: {
      offset: { offset: "94" }
    }
  });
}
const getActiveElement = function(document2) {
  document2 = document2 || window.document;
  if (document2.body === document2.activeElement || /* eslint-disable eqeqeq*/
  document2.activeElement.tagName == "IFRAME") {
    const iframes = document2.getElementsByTagName("iframe");
    for (let i = 0; i < iframes.length; i++) {
      const focused = getActiveElement(iframes[i].contentWindow.document);
      if (focused !== false) {
        return focused;
      }
    }
  } else return document2.activeElement;
  return false;
};
function isElementInViewport(el) {
  const rect = el.getBoundingClientRect();
  return rect.top >= 0 && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && rect.right <= (window.innerWidth || document.documentElement.clientWidth);
}
